/**
 * OmniLeagues — Registration hub (AD + Tech Specialist, identical).
 *
 * ONE top-level "Registration" menu whose sub-tabs mirror the live external
 * setup and read the existing OL datasets:
 *   Registrations · Programs · Financials · Invoices · Payments (+ Draft Board · Family Directory
 *   for the roles that had them as top-level menus — AJ 2026-10-14)
 *
 * Every panel already exists — this hub just composes them (same look as
 * OrganizationsHubPanel). Backend access is the shared `require_reg_admin`
 * guard (platform / AD / Tech Specialist); processor/deposit config stays
 * Tech-Specialist-only via `canConfigureProcessor`.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ClipboardList, Layers, DollarSign, FileText, CreditCard } from 'lucide-react';

import RegBuilderPanel from './RegBuilderPanel';
import ProgramsPanel from './ProgramsPanel';
import FinancialsPanel from './FinancialsPanel';
import InvoicingPanel from './InvoicingPanel';
import { PaymentsSettings } from './settings/PaymentsSettings';

const TABS = [
  { key: 'registrations', label: 'Registrations', icon: ClipboardList },
  { key: 'programs',      label: 'Programs',      icon: Layers },
  { key: 'financials',    label: 'Financials',    icon: DollarSign },
  { key: 'invoices',      label: 'Invoices',      icon: FileText },
];

const PLATFORM_TS = ['company_support', 'tech_specialist'];
// Payments (processor setup): Tech Specialist edits, AD + platform view read-only.
const PAYMENTS_ROLES = ['ad', 'tech_specialist', 'company_support', 'semprion_admin'];
const PAYMENTS_TAB = { key: 'payments', label: 'Payments', icon: CreditCard };

export function SubTabs({ tabs, active, setActive, isDark, testidPrefix }) {
  const inactive = isDark
    ? 'text-slate-400 hover:text-white hover:bg-slate-700/40'
    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100';
  const activeCls = isDark
    ? 'bg-amber-600/20 text-amber-400 border-amber-500/40'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`${testidPrefix}-tabs`}>
      {tabs.map(t => {
        const isActive = active === t.key;
        const Icon = t.icon;
        return (
          <Button key={t.key} variant="ghost" size="sm" onClick={() => setActive(t.key)}
                  className={`h-8 px-3 text-xs font-medium border ${isActive ? activeCls : 'border-transparent ' + inactive}`}
                  data-testid={`${testidPrefix}-tab-${t.key}`}>
            {Icon && <Icon className="h-3.5 w-3.5 mr-1.5" />}{t.label}
          </Button>
        );
      })}
    </div>
  );
}

// `extraTabs` [{key,label,icon,render}] — panels the Dashboard adds for some roles
// (AD: Draft Board, Family Directory — AJ 2026-10-14). `initialTab` deep-links a sub-tab.
export default function RegistrationHubPanel({ orgId, isDark = true, userRole, extraTabs = [], initialTab, onInitialConsumed }) {
  const [active, setActive] = useState(initialTab || 'registrations');
  // a deep link opens its sub-tab once, then is cleared so the hub opens normally next time
  useEffect(() => { if (initialTab) { setActive(initialTab); onInitialConsumed?.(); } }, [initialTab, onInitialConsumed]);
  const tabs = [...TABS, ...(PAYMENTS_ROLES.includes(userRole) ? [PAYMENTS_TAB] : []), ...extraTabs];
  const [newProgram, setNewProgram] = useState(false);
  const canConfigureProcessor = PLATFORM_TS.includes(userRole);

  if (!orgId) {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-6">
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization selected.</p>
        </CardContent>
      </Card>
    );
  }

  const navBg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';

  return (
    <div className="space-y-4" data-testid="registration-hub">
      <div className={`p-1.5 rounded-lg border ${navBg}`}>
        <SubTabs tabs={tabs} active={active} setActive={setActive} isDark={isDark} testidPrefix="reg-hub" />
      </div>

      {active === 'registrations' && <RegBuilderPanel orgId={orgId} isDark={isDark} userRole={userRole} startNew={newProgram} onStartConsumed={() => setNewProgram(false)} />}
      {active === 'programs' && <ProgramsPanel orgId={orgId} isDark={isDark} onCreateProgram={() => { setNewProgram(true); setActive('registrations'); }} />}
      {active === 'financials' && (
        <FinancialsPanel orgId={orgId} isDark={isDark} canConfigureProcessor={canConfigureProcessor} />
      )}
      {active === 'invoices' && <InvoicingPanel orgId={orgId} userRole={userRole} />}
      {active === 'payments' && PAYMENTS_ROLES.includes(userRole) && <PaymentsSettings orgId={orgId} isDark={isDark} />}
      {extraTabs.map((t) => (active === t.key ? <React.Fragment key={t.key}>{t.render()}</React.Fragment> : null))}
    </div>
  );
}
